import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AssetManager from '../services/AssetManager';
import logger from '../utils/logger';
import ScheduleEntry from '../models/ScheduleEntry';
import { useChangeTracking } from './useChangeTracking';
import { useSnackbar } from '../contexts/SnackbarContext';

type BaselineContext = {
    isLiveMode: boolean;
    draftName: string | null | undefined;
    selectedYear: number | null | undefined;
    selectedPeriodId?: string | number | null;
    selectedMonth?: any | null;
    selectedWeek?: any | null;
    onIdSwap?: (tempId: string, savedEntry: any) => void;
};

/**
 * useScheduleSaveManager centralizes:
 * - Baseline fetching from the API (via AssetManager)
 * - Baseline initialization in useChangeTracking (even when empty)
 * - Saving a single entry (create/update) with baseline merge
 * - Saving a set of entries using changeLog (create/update/delete) with baseline sync
 */
export function useScheduleSaveManager(user: any, ctx: BaselineContext) {
    const assetManager = useMemo(() => AssetManager.getInstance(), []);
    const { showSuccess, showError } = useSnackbar();

    // Build a stable baseline version key from context
    const baselineVersion = useMemo(() => {
        const draftSegment = ctx.isLiveMode ? 'live' : `draft:${ctx.draftName || 'unnamed'}`;
        const yearSegment = `year:${ctx.selectedYear ?? 'none'}`;
        const periodSegment = `period:${ctx.selectedPeriodId ?? 'none'}`;
        const monthSegment = `month:${(ctx.selectedMonth && ctx.selectedMonth.month) || 'none'}`;
        const weekSegment = `week:${(ctx.selectedWeek && ctx.selectedWeek.week) || 'none'}`;
        return `${draftSegment}-${yearSegment}-${periodSegment}-${monthSegment}-${weekSegment}`;
    }, [ctx.isLiveMode, ctx.draftName, ctx.selectedYear, ctx.selectedPeriodId, ctx.selectedMonth, ctx.selectedWeek]);

    // Local baseline state fetched from the server for the current context
    const [baselineItems, setBaselineItems] = useState<any[]>([]);
    const [isFetchingBaseline, setIsFetchingBaseline] = useState<boolean>(false);
    const [baselineError, setBaselineError] = useState<string | null>(null);
    const lastLoadedVersionRef = useRef<string | null>(null);

    // useChangeTracking without auto-baseline; we'll initialize manually so we can allow empty baselines
    const {
        changeLog,
        addLogEntry,
        calculateNetChanges,
        isChangeLogModalOpen,
        openChangeLogModal,
        closeChangeLogModal,
        getNetChanges,
        serverBaseline,
        initializeChangeTracking,
        mergeIntoBaseline,
        getChangeIdSets,
        resetChangeIdSets,
        // Undo/Redo functionality
        performUndo,
        performRedo,
        canUndo,
        canRedo,
        getUndoRedoState
    } = useChangeTracking(user);

    // Expose lightweight counters for pending changes via ID sets
    const getChangeIdCounts = useCallback(() => {
        try {
            const { createdIds, modifiedIds, deletedIds } = getChangeIdSets();
            return {
                created: createdIds.size,
                modified: modifiedIds.size,
                deleted: deletedIds.size
            };
        } catch (e) {
            return { created: 0, modified: 0, deleted: 0 };
        }
    }, [getChangeIdSets]);

    const hasPendingChangesByIdSets = useCallback(() => {
        const counts = getChangeIdCounts();
        return (counts.created + counts.modified + counts.deleted) > 0;
    }, [getChangeIdCounts]);

    const fetchBaseline = useCallback(async () => {
        setIsFetchingBaseline(true);
        setBaselineError(null);
        try {
            const filters: any = {};
            if (!ctx.isLiveMode && ctx.draftName) {
                filters.draft_name = ctx.draftName;
            }
            if (ctx.selectedPeriodId) {
                filters.timePeriodId = ctx.selectedPeriodId;
            }
            if (ctx.selectedYear != null) {
                filters.year = ctx.selectedYear;
            }

            logger.debug('[useScheduleSaveManager] Fetching baseline with filters:', filters);
            const entries = await assetManager.fetchScheduleEntries(filters);
            setBaselineItems(entries);

            // Initialize baseline inside useChangeTracking even if empty
            initializeChangeTracking(entries);
            lastLoadedVersionRef.current = baselineVersion;
            logger.debug('[useScheduleSaveManager] Baseline initialized:', {
                count: entries.length,
                version: baselineVersion
            });
        } catch (err: any) {
            logger.error('[useScheduleSaveManager] Failed to fetch baseline:', err);
            setBaselineError(err?.message || 'Failed to fetch baseline');
        } finally {
            setIsFetchingBaseline(false);
        }
    }, [assetManager, ctx.isLiveMode, ctx.draftName, ctx.selectedPeriodId, ctx.selectedYear, initializeChangeTracking, baselineVersion]);

    // Refresh baseline when the context key changes
    useEffect(() => {
        if (lastLoadedVersionRef.current !== baselineVersion) {
            // fetchBaseline();
        }
    }, [baselineVersion, fetchBaseline]);


    // Save a single entry (create or update). Returns the saved ScheduleEntry.
    const saveSingleEntry = useCallback(async (editedDataObj: any, originalItem?: any) => {
        // Determine if create vs update
        const isAddOperation = originalItem && originalItem.id && String(originalItem.id).startsWith('temp-add-');

        // Normalize to ScheduleEntry shape, attach draft name when in draft mode
        const entryData = { ...(editedDataObj || {}) } as any;
        if (!ctx.isLiveMode && ctx.draftName) {
            (entryData as any).draft_name = ctx.draftName;
        }

        const entryObj = new ScheduleEntry(entryData);

        let savedEntry: ScheduleEntry;
        if (isAddOperation) {
            // For creation, ensure no temp id is sent
            const createData = entryObj.toObject();
            delete (createData as any).id;
            savedEntry = await assetManager.createScheduleEntry(createData);
        } else {
            savedEntry = await assetManager.updateScheduleEntry(entryObj.id, entryObj);
        }

        // Merge the saved entry into baseline for immediate consistency
        try {
            mergeIntoBaseline([savedEntry]);
        } catch (e) {
            logger.warn('[useScheduleSaveManager] mergeIntoBaseline failed after single save:', e);
        }

        return savedEntry;
    }, [assetManager, ctx.isLiveMode, ctx.draftName, mergeIntoBaseline]);

    // Save the entire set based on computed net changes from baseline vs current schedule
    const saveEntireSet = useCallback(async (currentScheduleItems: any[], _unusedChangeLogEntries?: any[]) => {
        logger.debug('[useScheduleSaveManager] saveEntireSet (ID-set mode) called', {
            currentLen: currentScheduleItems?.length || 0,
            baselineVersion
        });

        // Read tracked IDs from change tracking
        const { createdIds, modifiedIds, deletedIds } = getChangeIdSets();

        const createdIdsArr = Array.from(createdIds);
        const modifiedIdsArr = Array.from(modifiedIds);
        const deletedIdsArr = Array.from(deletedIds);

        // Resolve items by ID from the provided current list
        const byId = new Map<string, any>((Array.isArray(currentScheduleItems) ? currentScheduleItems : []).map((it: any) => [String(it?.id), it]));
        const createdItems = createdIdsArr.map(id => byId.get(String(id))).filter(Boolean);
        const modifiedItems = modifiedIdsArr.map(id => byId.get(String(id))).filter(Boolean);

        logger.debug('[useScheduleSaveManager] Persisting by change sets', {
            createCount: createdItems.length,
            modifyCount: modifiedItems.length,
            deleteCount: deletedIdsArr.length
        });

        const promises: Promise<any>[] = [];

        // Show succinct overview snackbar before saving
        const createCount = createdItems.length;
        const modifyCount = modifiedItems.length;
        const deleteCount = deletedIdsArr.length;
        const totalCount = createCount + modifyCount + deleteCount;

        // Deletions first (skip temp IDs that never existed server-side)
        deletedIdsArr.forEach((id) => {
            const idStr = String(id);
            if (!idStr.startsWith('temp-') && !idStr.startsWith('item-') && !idStr.startsWith('temp-add-')) {
                promises.push(assetManager.deleteScheduleEntry(idStr));
            }
        });

        // Creates (ensure draft name when in draft mode, strip temp id)
        const createPairs: Array<Promise<{ tempId: string, saved: any }>> = [];
        createdItems.forEach((item: any) => {
            const data = item instanceof ScheduleEntry ? item.toObject() : { ...item };
            const tempId = String((item as any)?.id);
            if (!ctx.isLiveMode && ctx.draftName) {
                (data as any).draft_name = ctx.draftName;
            }
            delete (data as any).id;
            const p = assetManager.createScheduleEntry(data).then(saved => ({ tempId, saved }));
            createPairs.push(p);
            promises.push(p as unknown as Promise<any>);
        });

        // Modifies (ensure draft name when in draft mode)
        modifiedItems.forEach((item: any) => {
            const data = item instanceof ScheduleEntry ? item : new ScheduleEntry(item);
            if (!ctx.isLiveMode && ctx.draftName) {
                (data as any).draft_name = ctx.draftName;
            }
            promises.push(assetManager.updateScheduleEntry((data as any).id, data));
        });

        let results: any[];
        try {
            results = await Promise.all(promises);
        } catch (err: any) {
            const backendMessage = (err && (err.response?.data?.error || err.message)) || 'Save failed';
            try { showError(backendMessage); } catch {}
            throw err;
        }

        // After creates resolve, swap temp IDs to server IDs in tracking and optionally UI state
        try {
            const createResults = await Promise.all(createPairs);
            const savedEntries: any[] = [];
            createResults.forEach(({ tempId, saved }) => {
                if (saved && saved.id) {
                    // Notify owner (state hook) to swap in UI state; still merge baseline locally
                    if (typeof ctx.onIdSwap === 'function') {
                        try { ctx.onIdSwap(String(tempId), saved); } catch {}
                    }
                    savedEntries.push(saved);
                }
            });
            if (savedEntries.length > 0) {
                mergeIntoBaseline(savedEntries);
            }
        } catch (e) {
            logger.warn('[useScheduleSaveManager] Post-create ID swap failed:', e);
        }

        // Keep baseline in sync
        try {
            if (createdItems.length + modifiedItems.length > 0) {
                mergeIntoBaseline([...createdItems, ...modifiedItems]);
            }
            if (deletedIdsArr.length > 0) {
                setBaselineItems(prev => prev.filter(e => !deletedIds.has(String(e?.id))));
            }
        } catch (e) {
            logger.warn('[useScheduleSaveManager] Local baseline sync warning:', e);
        }

        // Reset tracked change IDs on success
        try { resetChangeIdSets(); } catch { /* noop */ }

        // Explicitly clear the change log after successful save
        try {
            // Force clear the change log by calling initializeChangeTracking with current items
            // This ensures the baseline matches the current state and change log is cleared
            initializeChangeTracking(currentScheduleItems);
        } catch (e) {
            logger.warn('[useScheduleSaveManager] Failed to clear change log after save:', e);
        }

        // Success snackbar after saving
        if (totalCount > 0) {
            try {
                showSuccess(`Saved ${createCount + modifyCount + deleteCount} changes`);
            } catch {}
        }

        return results;
    }, [assetManager, ctx.isLiveMode, ctx.draftName, getChangeIdSets, resetChangeIdSets, mergeIntoBaseline, setBaselineItems, baselineVersion, initializeChangeTracking]);

    // Manager-level handleSaveChanges: compute changes and persist; then update baseline/reset
    const handleSaveChanges = useCallback(async (currentScheduleItems: any[], customSaveHandler?: (items: any[], changes: any[]) => Promise<any>, onResetComplete?: () => void) => {
        logger.debug('[useScheduleSaveManager] handleSaveChanges start', {
            currentLen: currentScheduleItems?.length || 0,
            baselineVersion
        });

        // Persist using ID sets tracking; ignore net-changes
        const persist = async () => {
            if (customSaveHandler && typeof customSaveHandler === 'function') {
                // Maintain signature but pass empty changes (we rely on ID sets)
                return customSaveHandler(currentScheduleItems, []);
            }
            return saveEntireSet(currentScheduleItems, []);
        };

        await persist();

        try {
            // After successful save, reset local baseline state
            setBaselineItems(prev => prev);
        } catch (e) {
            logger.warn('[useScheduleSaveManager] Baseline post-save sync warning:', e);
        }

        if (onResetComplete && typeof onResetComplete === 'function') {
            onResetComplete();
        }
        // Note: Change log clearing is now handled in saveEntireSet to ensure it happens immediately after save
    }, [saveEntireSet, baselineVersion, initializeChangeTracking]);

    // Backward-compatible alias for components expecting this name
    const saveChangesToAPI = saveEntireSet;

    return {
        // Change tracking passthrough
        changeLog,
        addLogEntry,
        calculateNetChanges,
        isChangeLogModalOpen,
        openChangeLogModal,
        closeChangeLogModal,
        handleSaveChanges,
        serverBaseline,
        getChangeIdCounts,
        hasPendingChangesByIdSets,

        // Manager extras
        saveSingleEntry,
        handleSaveSchedule: saveSingleEntry,
        saveEntireSet,
        saveChangesToAPI,
        isFetchingBaseline,
        baselineError,
        baselineVersion,

        // Undo/Redo functionality
        performUndo,
        performRedo,
        canUndo,
        canRedo,
        getUndoRedoState
    };
}

export default useScheduleSaveManager;


